import React, { useState } from 'react'
import cn from 'classnames'
import { Button, IconCloseDefault } from '@mc/design-system'
import { withI18nContext, useTranslation } from '@mc/i18n'
import i18nInstance from './i18n/i18nInstance'
import { DEFAULT_THEME, isDarkTheme, isLightTheme, type Themes } from './utils'
import styles from './Banner.module.scss'

const Banner = ({
  isClosable = false,
  onClose = () => {},
  children,
  className,
  position = 'relative',
  theme = DEFAULT_THEME,
  closeBtnClassName,
  ...props
}: BannerProps) => {
  const { t } = useTranslation('@mc/banner')
  const [visible, setVisible] = useState(true)
  const handleClose = () => {
    setVisible(false)
    onClose()
  }

  return (
    <div
      className={cn(
        'd-flex align-items-center justify-content-center mc-overflow--hidden',
        { 'mc-background--color-light mc-theme-light': isLightTheme(theme) },
        { 'mc-background--color-dark mc-theme-dark': isDarkTheme(theme) },
        `position-${position}`,
        className,
        styles.banner,
        { [styles.boxShadowLight]: isLightTheme(theme) && visible },
        { [styles.bannerHidden]: !visible },
      )}
      role='region'
      {...props}
    >
      <>
        {children}
        {isClosable && (
          <Button
            aria-label={t('bannerClose')}
            className={cn('mc-p-0', styles.bannerClose, closeBtnClassName)}
            kind='link'
            onClick={handleClose}
          >
            <IconCloseDefault size='sm' className='mc-m-2' />
          </Button>
        )}
      </>
    </div>
  )
}

type BannerProps = {
  children?: React.ReactNode | React.ReactNode[]
  isClosable?: boolean
  onClose?: () => void
  className?: string
  position?: 'relative' | 'sticky'
  theme?: Themes
  closeBtnClassName?: string
}

export default withI18nContext(i18nInstance)(Banner)
